import axios from 'axios';

import { healioSpaceAPIURL } from './baseHealioSpaceAPI';
import { installSessionInterceptors } from './sessionInterceptors';
// Client list item for displaying in lists
export interface ClientListItem {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: 'active' | 'inactive' | 'pending';
  lastActivityDate?: string;
  healthScore?: number;
  avatarUrl?: string;
  fullName: string;
}

// Health summary for client detail view
export interface ClientHealthSummary {
  latestHeartRate?: number;
  latestHrv?: number;
  latestSleepScore?: number;
  latestRecoveryScore?: number;
  lastMetricsDate?: string;
}

// Full client profile for detail view
export interface ClientProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  dateOfBirth?: string;
  profilePictureUrl?: string;
  status: 'active' | 'inactive' | 'pending';
  assignedPractitionerId?: string;
  assignedDate?: string;
  healthSummary?: ClientHealthSummary;
  fullName: string;
}

// Update client DTO
export interface UpdateClientDto {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  dateOfBirth?: string;
}

// Paginated response interface (HA-164, HA-171)
export interface PaginatedResponse<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  // Cursor tokens for optimal DynamoDB pagination (HA-171)
  nextCursor?: string;
  prevCursor?: string;
}

// Pagination parameters for client list (HA-164)
export interface ClientPaginationParams {
  page?: number;
  pageSize?: number;
  search?: string;
}

// Cursor pagination parameters for optimal DynamoDB performance (HA-171)
export interface ClientCursorPaginationParams {
  pageSize?: number;
  cursor?: string;
}

// Create axios instance for client API
export const clientApi = axios.create({
  baseURL: `${healioSpaceAPIURL}/api/clients`,
  withCredentials: true,
});

// Add authorization header to all requests
installSessionInterceptors(clientApi);

/**
 * Get all clients for the current practitioner (non-paginated)
 * @param searchQuery Optional search query to filter clients
 */
export const getClients = async (searchQuery?: string): Promise<ClientListItem[]> => {
  try {
    const params = searchQuery ? { search: searchQuery } : {};
    const response = await clientApi.get('', { params });

    return response.data;
  } catch (error) {
    console.error('Error fetching clients:', error);

    return [];
  }
};

/**
 * Get paginated clients for the current practitioner (HA-164)
 * @param params Pagination parameters (page, pageSize, search)
 */
export const getClientsPaginated = async (
  params: ClientPaginationParams = {}
): Promise<PaginatedResponse<ClientListItem>> => {
  try {
    const { page = 1, pageSize = 25, search } = params;
    const queryParams: Record<string, string | number> = { page, pageSize };

    if (search) {
      queryParams.search = search;
    }
    const response = await clientApi.get('/paginated', { params: queryParams });

    return response.data;
  } catch (error) {
    console.error('Error fetching paginated clients:', error);

    // Return empty paginated response on error
    return {
      items: [],
      page: params.page || 1,
      pageSize: params.pageSize || 25,
      totalCount: 0,
      totalPages: 0,
      hasPreviousPage: false,
      hasNextPage: false,
    };
  }
};

/**
 * Get cursor-paginated clients for optimal DynamoDB performance (HA-171)
 * Use this for large client lists (1000+) where page navigation is sequential
 * @param params Cursor pagination parameters (pageSize, cursor)
 */
export const getClientsPaginatedWithCursor = async (
  params: ClientCursorPaginationParams = {}
): Promise<PaginatedResponse<ClientListItem>> => {
  try {
    const { pageSize = 25, cursor } = params;
    const queryParams: Record<string, string | number> = { pageSize };

    if (cursor) {
      queryParams.cursor = cursor;
    }
    const response = await clientApi.get('/paginated/cursor', { params: queryParams });

    return response.data;
  } catch (error) {
    console.error('Error fetching cursor-paginated clients:', error);

    // Return empty paginated response on error
    return {
      items: [],
      page: -1, // -1 indicates cursor mode
      pageSize: params.pageSize || 25,
      totalCount: 0,
      totalPages: 0,
      hasPreviousPage: false,
      hasNextPage: false,
    };
  }
};

/**
 * Get a specific client's details
 */
export const getClientById = async (clientId: string): Promise<ClientProfile | null> => {
  try {
    // HA-349: encode so a `#` in a composite USER#{email} id is not parsed as a
    // URL fragment (which truncated the path -> GET /clients/USER -> 404).
    const response = await clientApi.get(`/${encodeURIComponent(clientId)}`);

    return response.data;
  } catch (error) {
    console.error('Error fetching client details:', error);

    return null;
  }
};

/**
 * Update a client's profile
 */
export const updateClient = async (
  clientId: string,
  updateData: UpdateClientDto
): Promise<boolean> => {
  try {
    // HA-349: encode the composite id so the `#` survives the request path.
    await clientApi.put(`/${encodeURIComponent(clientId)}`, updateData);

    return true;
  } catch (error) {
    console.error('Error updating client:', error);

    return false;
  }
};

/**
 * Assign a client to the current practitioner
 */
export const assignClient = async (clientId: string): Promise<boolean> => {
  try {
    // HA-349: encode the composite id so the `#` survives the request path.
    await clientApi.post(`/${encodeURIComponent(clientId)}/assign`);

    return true;
  } catch (error) {
    console.error('Error assigning client:', error);

    return false;
  }
};

/**
 * Check if the current practitioner has access to a client
 */
export const checkClientAccess = async (clientId: string): Promise<boolean> => {
  try {
    // HA-349: encode the composite id so the `#` survives the request path.
    const response = await clientApi.get(`/${encodeURIComponent(clientId)}/access`);

    return response.data === true;
  } catch (error) {
    console.error('Error checking client access:', error);

    return false;
  }
};
